<template>
    <div class="copy-field" :class="classes">
        <label v-if="label">{{ label }}</label>
        <div class="input-group">
            <div v-if="icon" class="input-group-addon">
                <span class="input-group-text"><i :class="`${iconType} fa-${icon}`"></i></span>
            </div>
            <input
                class="form-control"
                :id="inputId"
                :name="name"
                :type="inputType"
                :value="userValue"
                :readonly="readonly"
                @input="doUserInput"
            >
            <span class="input-group-btn" :class="{'is-disabled': inputDisabled}">
                <button
                    type="button"
                    ref="copybutton"
                    class="btn"
                    :class="buttonClasses"
                    :data-clipboard-target="`#${inputId}`"
                    :content="isCopied ? 'Copied' : 'Copy'"
                    :disabled="inputDisabled"
                    v-tippy="{arrow: true}">
                    <i class="fas" :class="{'fa-check': isCopied, 'fa-copy': !isCopied}"></i>
                </button>
                <qr-code-btn
                    v-if="qrCode"
                    direction="right"
                    content="Download QR code"
                    :disabled="inputDisabled"
                    v-tippy="{arrow: true}"
                    v-on:download-qr-code="doDownloadQRCode"
                />
            </span>
        </div>
    </div>
</template>

<script>
import { uniqueId } from 'lodash';
import Clipboard from 'clipboard';

export default {
    name: 'CopyField',
    props: {
        value: {
            type: String,
            required: true
        },
        timeout: {
            type: Number,
            default: 5000
        },
        icon: {
            type: String,
            default: null,
        },
        iconType: {
            type: String,
            default: 'fas',
        },
        label: {
            type: String,
        },
        inputType: {
            type: String,
            default: 'text',
        },
        qrCode: {
            type: Boolean,
            default: false,
        },
        qrCodeTitle: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: true,
        },
        name: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            clipboard: null,
            isCopied: false,
            userValue: null,
            inputDisabled: false,
        }
    },
    mounted() {
        this.clipboard = new Clipboard(this.$refs.copybutton);

        this.clipboard.on('success', (e) => {
            this.isCopied = true;
            e.clearSelection();
            setTimeout(() => this.isCopied = false, this.timeout);
        });
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                this.userValue = newValue;
            },
        }
    },
    computed: {
        inputId() {
            return uniqueId('copybutton_');
        },
        classes() {
            return {
                'is-copied': this.isCopied,
            }
        },
        buttonClasses() {
            return {
                'btn-default': !this.isCopied,
                'btn-primary':  this.isCopied,
            }
        },
    },
    methods: {
        doDownloadQRCode(e) {
            this.$emit('download-qr-code', {
                type: e,
                value: this.value || this.userValue,
                title: this.qrCodeTitle,
            });
        },
        doUserInput(e) {
            const value = e.target.value;
            this.userValue = value;
            if (value) {
                this.inputDisabled = false;
            } else {
                this.inputDisabled = true;
            }
        },
    },
    destroyed() {
        this.clipboard.destroy();
    }
}
</script>

<style lang="scss" scoped>
@import '~/sass/_variables';

.copy-field {
    position: relative;

    &.is-copied:before {
        display: flex;
        position: absolute;
        align-items: center;
        justify-content: center;
        content: "";
        width: calc(100% - 46px); // button width
        height: 100%;
        top: 0;
        left: 0;
        z-index: 999;
        background-color: rgba(white, 0.6);
    }

    .input-group-btn .btn + .qr-code-btn {
        border-left: solid 1px $gray-light;
    }
}
</style>
